import { clsx } from "clsx";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import {
  AccessibilityAccordionCollection,
  AccessibilityProfiles,
  FeedBackUser,
  FlyoutContainer,
  Header,
  MainFlyoutWidgetActionButtons,
  MainFlyoutWidgetFooter,
  Modal,
  ToggleSwitch,
  WidgetIcon,
  WidgetToggle,
} from "./components";
import {
  DEFAULT_ICON_SIZE,
  MAX_SCREEN_PERCENTAGE,
  WIDGET_IFRAME_MOBILE_BREAKPOINT,
} from "./constants";
import { useSettings } from "./context/SettingsContext";
import { useLanguageManagement } from "./hooks";
import xlicon from "./assets/image/XL.png";
import "./assets/styles/app-widget.css";
import {
  getDefaultIconSizeConfig,
  useIconSize,
  useMobileWidth,
} from "./utils/widget.helper";
import {
  postModalCloseMessage,
  postFlyoutToggleMessage,
  postInterfaceHideMessage,
  postNavigationPageStructureMessage,
  postInterfaceResetMessage,
  postFeatureSelectMessage,
  postFeatureUnselectMessage,
} from "./utils/message.helper";
import { Features } from "./constants/features";
import { Widget } from "./types/widget";
import { useWidgetKeyboardShortcuts } from "./hooks/useWidgetKeyboardShortcuts";

declare global {
  interface Window {
    google: any;
    googleTranslateElementInit: () => void;
  }
}

interface Language {
  code: string;
  name: string;
  url: string;
}

const MainFlyoutWidget: React.FC = () => {
  const isLargeScreen = window.innerWidth >= WIDGET_IFRAME_MOBILE_BREAKPOINT;
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isRight, setIsRight] = useState(false);
  const previousDistanceRef = useRef<string | undefined>(undefined);
  const [flyoutStyle, setFlyoutStyle] = useState<React.CSSProperties>({
    top: "20px",
    left: "20px",
  });
  const [iconSize, setIconSize] = useState<number>(DEFAULT_ICON_SIZE);

  const [isFeedbackActive, setIsFeedbackActive] = useState(false);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef<{
    startX: number;
    startY: number;
    initialLeft: number;
    initialTop: number;
  } | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null); // Added dropdown ref
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState("headings");
  const [widgetData, setWidgetData] = useState<Widget | null>(null);
  const {
    oversizeWidget,
    setOversizeWidget,
    setButtonColor,
    selectedLanguage,
    setSelectedLanguage,
    isRight: existingIsRight,
    setIsRight: setExistingIsRight,
    setButtonTextColor,
    setWidgetButtonColor,
    setWidgetButtonTextColor,
    setWidgetColor,
    setWidgetTextColor,
    widgetDataSettings,
    buttonColor,
    horizontalDistance,
  } = useSettings();
  const { t } = useTranslation();
  const { availableLanguages, handleSaveData } = useLanguageManagement();
  const isMobile = window.innerWidth < WIDGET_IFRAME_MOBILE_BREAKPOINT;

  const inputRef = useRef<HTMLInputElement>(null);


  const desktopDistance =
    widgetDataSettings?.settings?.position?.positionOpen?.desktop?.distance;
  const desktopHorizontalDistance =
    desktopDistance != null ? `${desktopDistance}px` : undefined;


  useEffect(() => {
    const isLargeScreen = window.innerWidth >= WIDGET_IFRAME_MOBILE_BREAKPOINT;
    if (!isLargeScreen) {
      setFlyoutStyle({ top: 0, left: 0, right: 0, bottom: 0 });
      return;
    };

    setFlyoutStyle((prev) => ({
      ...prev,
      left: isRight ? "auto" : horizontalDistance,
      right: isRight ? horizontalDistance : "auto",
    }));
  }, [horizontalDistance, isRight, widgetData]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.action === "resize-widget") {
        
        const { isMobile } = event.data;
        const newIconSize = isMobile
          ? widgetData?.settings?.appearance?.iconSize?.mobile
          : widgetData?.settings?.appearance?.iconSize?.desktop;
        setIconSize(newIconSize);
      }

      if (event.data?.action === "update-isMobile") {
        // Update flyout position based on window width
        if (window.innerWidth >= WIDGET_IFRAME_MOBILE_BREAKPOINT) {
          setFlyoutStyle({ top: "20px", left: "auto", right: horizontalDistance });
        } else {
          setFlyoutStyle({ top: 0, left: 0, right: 0, bottom: 0 });
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [iconSize, widgetData]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (document.activeElement === inputRef.current) {
        if (event.key === "Tab") {
          // Prevent default tab behavior if you want to trap focus
          // event.preventDefault();
          // Add custom tab logic here if needed
        }

        if (event.key === "Enter") {
          event.preventDefault();
          setOversizeWidget(!oversizeWidget);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [oversizeWidget, setOversizeWidget]);

  const resetSettings = () => {
    postInterfaceResetMessage();
    // Reset the language to the default from widgetData
    if (widgetData?.settings?.advanced?.defaultLanguage) {
      const defaultLanguage = (availableLanguages ?? []).find(
        (lang) => lang.code === widgetData.settings.advanced.defaultLanguage
      );
      if (defaultLanguage) {
        setSelectedLanguage(defaultLanguage);
        handleSaveData(defaultLanguage.code);
      }
    }
    localStorage.clear();
    // Reset the position of the flyout
    const isLargeScreen =
      window.innerWidth >= WIDGET_IFRAME_MOBILE_BREAKPOINT;

    if (isLargeScreen) {
      const desktopPosition =
        widgetData?.settings?.position?.positionOpen?.desktop;
      if (desktopPosition) {
        const { anchor, distance } = desktopPosition;
        const cssDistance = `${distance}px`;
        setIsRight(anchor === "right");
        setExistingIsRight(anchor === "right");
        setFlyoutStyle({
          top: "20px",
          left: anchor === "right" ? "auto" : cssDistance,
          right: anchor === "right" ? cssDistance : "auto",
        });
      } else {
        // Fallback to a default if settings are not available
        setIsRight(false);
        setExistingIsRight(false);
        setFlyoutStyle({ top: "20px", left: "20px", right: "auto" });
      }
    } else {
      // Full screen on mobile
      setFlyoutStyle({ top: 0, left: 0, right: 0, bottom: 0 });
    }
    setOversizeWidget(false);
    postFeatureUnselectMessage(Features.COLOR_SATURATION);
    postFeatureUnselectMessage(Features.CONTENT_DYSLEXIC_FRIENDLY);
    postFeatureUnselectMessage(Features.NAVIGATION_BIG_CURSOR);
    postFeatureUnselectMessage(Features.NAVIGATION_TOOLTIPS);
    postFeatureUnselectMessage(Features.NAVIGATION_PAUSE_ANIMATION);
    postFeatureUnselectMessage(Features.NAVIGATION_BIG_CURSOR);
    postFeatureUnselectMessage(Features.NAVIGATION_READING_MASK);
    postFeatureUnselectMessage(Features.NAVIGATION_READING_GUIDE);
    postFeatureUnselectMessage(Features.COLOR_SMART_CONTRAST);
    postFeatureUnselectMessage(Features.NAVIGATION_MUTE_SOUND);
    postFeatureUnselectMessage(Features.NAVIGATION_VOICE_NAVIGATION);
    postFeatureUnselectMessage(Features.NAVIGATION_SCREEN_READER);
    postFeatureUnselectMessage(Features.NAVIGATION_DARK_CURSOR);
    postFeatureUnselectMessage(Features.COLOR_LIGHT_CONTRAST);
    postFeatureUnselectMessage(Features.PROFILE_COLOR_BLIND);
    postFeatureUnselectMessage(Features.PROFILE_COGNITIVE_LEARNING);
  };

  useWidgetKeyboardShortcuts({
    isFlyoutOpen,
    setIsFlyoutOpen,
    setIsDropdownOpen,
    resetSettings,
    inputRef,
    oversizeWidget,
    setOversizeWidget,
  });

  // Memoized reset settings handler
  const handleClick = () => {
    resetSettings();
    // clear all the local storage
    localStorage.clear();
  };

  const toggleFlyout = () => {
    setShowLoader(true);
    if (!isFlyoutOpen) {
      // Try to get saved position from localStorage first
      const savedStyle = localStorage.getItem("flyoutStyle");
      if (savedStyle) {
        setFlyoutStyle(JSON.parse(savedStyle));
      } else {
        const isLargeScreen =
          window.innerWidth >= WIDGET_IFRAME_MOBILE_BREAKPOINT;
        if (isLargeScreen) {
          setFlyoutStyle({
            top: "20px",
            left: isRight ? "auto" : horizontalDistance,
            right: isRight ? horizontalDistance : "auto",
          });
        } else {
          setFlyoutStyle({ top: 0, left: 0, right: 0, bottom: 0 });
        }
      }

      const selectedItems = JSON.parse(
        localStorage.getItem("selectedItems") || "[]"
      );

      if (selectedItems.includes(Features.NAVIGATION_READING_MASK)) {
        postFeatureSelectMessage(Features.NAVIGATION_READING_MASK);
      }
      if (selectedItems.includes(Features.NAVIGATION_READING_GUIDE)) {
        postFeatureSelectMessage(Features.NAVIGATION_READING_GUIDE);
      }
    }
    postFlyoutToggleMessage(!isFlyoutOpen);
    setTimeout(() => {
      setIsFlyoutOpen(!isFlyoutOpen);
    }, 50);
    setShowLoader(false);
  };

  const closeModal = () => setIsModalOpen(false);

  const startDrag = (e: React.MouseEvent) => {
    if (!flyoutRef.current) return;
    setIsDragging(true);
    flyoutRef.current.style.transition = "none";
    flyoutRef.current.style.cursor = "grabbing";

    const initialRect = flyoutRef.current.getBoundingClientRect();
    dragStartRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      initialLeft: initialRect.left,
      initialTop: initialRect.top,
    };
  };

  const onDrag = (e: MouseEvent) => {
    if (!isDragging || !flyoutRef.current || !dragStartRef.current) return;

    const { startX, startY, initialLeft, initialTop } = dragStartRef.current;

    let newX = initialLeft + (e.clientX - startX);
    let newY = initialTop + (e.clientY - startY);

    const rect = flyoutRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    newX = Math.max(0, Math.min(newX, viewportWidth - rect.width));
    newY = Math.max(0, Math.min(newY, viewportHeight - rect.height));

    setFlyoutStyle({
      left: `${newX}px`,
      top: `${newY}px`,
      right: "auto",
      bottom: "auto",
    });
  };
  const endDrag = () => {
    setIsDragging(false);
    dragStartRef.current = null;
    if (flyoutRef.current) {
      flyoutRef.current.style.transition = ""; // Re-enable transitions
      flyoutRef.current.style.cursor = "grab";

      const finalRect = flyoutRef.current.getBoundingClientRect();
      const finalStyle = {
        left: `${finalRect.left}px`,
        top: `${finalRect.top}px`,
        right: "auto",
        bottom: "auto",
      };
      setFlyoutStyle(finalStyle);
      localStorage.setItem("flyoutStyle", JSON.stringify(finalStyle));
    }
  };

  const getFlyoutPosition = (location: number) => {
    if (!flyoutRef.current) return { x: 0, y: 0 };
    const positions = [
      { x: 0, y: 0 },
      { x: window.innerWidth / 2 - flyoutRef.current.offsetWidth / 2, y: 0 },
      { x: window.innerWidth - flyoutRef.current.offsetWidth, y: 0 },
      { x: 0, y: window.innerHeight / 2 - flyoutRef.current.offsetHeight / 2 },
      {
        x: window.innerWidth / 2 - flyoutRef.current.offsetWidth / 2,
        y: window.innerHeight / 2 - flyoutRef.current.offsetHeight / 2,
      },
      {
        x: window.innerWidth - flyoutRef.current.offsetWidth,
        y: window.innerHeight / 2 - flyoutRef.current.offsetHeight / 2,
      },
      { x: 0, y: window.innerHeight - flyoutRef.current.offsetHeight },
      {
        x: window.innerWidth / 2 - flyoutRef.current.offsetWidth / 2,
        y: window.innerHeight - flyoutRef.current.offsetHeight,
      },
      {
        x: window.innerWidth - flyoutRef.current.offsetWidth,
        y: window.innerHeight - flyoutRef.current.offsetHeight,
      },
    ];
    return positions[location];
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", onDrag);
      document.addEventListener("mouseup", endDrag);
    }
    return () => {
      document.removeEventListener("mousemove", onDrag);
      document.removeEventListener("mouseup", endDrag);
    };
  }, [isDragging]);

  useEffect(() => {
    if (isFlyoutOpen) {
      // Try to get saved position from localStorage
      const savedStyle = localStorage.getItem("flyoutStyle");
      if (savedStyle) {
        setFlyoutStyle(JSON.parse(savedStyle));
      }
    }
  }, [isFlyoutOpen, existingIsRight]);

  // Click outside handler for flyout and modal
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        flyoutRef.current &&
        !flyoutRef.current.contains(e.target as Node) &&
        (!modalRef.current || !modalRef.current.contains(e.target as Node))
      ) {
        setIsFlyoutOpen(false);
        postFlyoutToggleMessage(false);
      }
    };

    if (isFlyoutOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isFlyoutOpen]);

  useEffect(() => {
    window.addEventListener("message", (event) => {
      if (event.data.action === "load-widget") {
        const eventWidgetData = event.data.data;
        const widgetData = eventWidgetData?.widget as Widget;
        setWidgetData(widgetData);

        setButtonColor(
          widgetData?.settings?.appearance?.colors?.buttonColor ||
            "#0048FF"
        );
        setButtonTextColor(
          widgetData?.settings?.appearance?.colors
            ?.buttonColor || "#FFFFFF"
        );
        setWidgetButtonColor(
          widgetData?.settings?.appearance?.colors
          ?.buttonColor ||
            "#0048FF"
        );
        setWidgetButtonTextColor(
          widgetData?.settings?.appearance?.colors
            ?.buttonColor || "#FFFFFF"
        );
        setWidgetColor(
          widgetData?.settings?.appearance?.colors
          ?.buttonColor ||
            "#0048FF"
        );
        setWidgetTextColor(
          widgetData?.settings?.appearance?.colors
            ?.buttonColor || "#FFFFFF"
        );
      }
    });
  }, []);

  useEffect(() => {
    if (
      widgetData?.settings?.position?.positionClosed?.desktop?.position !==
        undefined &&
      flyoutRef.current
    ) {
      const position = getFlyoutPosition(
        widgetData.settings.position.positionClosed.desktop.position
      );
      setFlyoutStyle((prev) => ({
        ...prev,
        left: `${position.x}px`,
        top: `${position.y}px`,
      }));
    }
  }, [widgetData]);

  useEffect(() => {
    const handleResize = () => {
      const isLargeScreen =
        window.innerWidth >= WIDGET_IFRAME_MOBILE_BREAKPOINT;

      if (isLargeScreen) {
        setFlyoutStyle({
          top: "20px",
          left: isRight ? "auto" : horizontalDistance,
          right: isRight ? horizontalDistance : "auto",
        });
      } else {
        setFlyoutStyle({ top: 0, left: 0, right: 0, bottom: 0 });
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [horizontalDistance, isRight]);

  useEffect(() => {
    const trySetDefaultLanguage = async () => {
      const defaultLangCode =
        widgetData?.settings?.advanced?.defaultLanguage || "german";

      if (availableLanguages.length === 0) return;

      const fallbackLanguage = availableLanguages.find(
        (lang) => lang.code === defaultLangCode
      );

      if (fallbackLanguage) {
        setSelectedLanguage(fallbackLanguage);
        await handleSaveData(defaultLangCode);
      } else {
        const germanLang = availableLanguages.find(
          (lang) => lang.code === "german"
        );
        if (germanLang) {
          setSelectedLanguage(germanLang);
          await handleSaveData("german");
        }
      }
    };

    trySetDefaultLanguage();
  }, [availableLanguages, widgetData?.settings?.advanced?.defaultLanguage]);

  const getMaxHorizontalDistance = useCallback(() => {
    if (!flyoutRef.current) return 0;
    const viewportWidth = window.innerWidth;
    const widgetWidth = flyoutRef.current.offsetWidth;
    const availableSpace = viewportWidth - widgetWidth;
    return Math.floor(availableSpace * MAX_SCREEN_PERCENTAGE);
  }, []);

  const getAdjustedHorizontalDistance = useCallback(
    (distance: number | undefined | null) => {
      if (!distance) return "20px";
      const maxDistance = getMaxHorizontalDistance();
      return `${Math.min(distance, maxDistance)}px`;
    },
    [getMaxHorizontalDistance]
  );

  // Handle side switching when distance is too large
  useEffect(() => {
    if (
      !desktopHorizontalDistance ||
      desktopHorizontalDistance === previousDistanceRef.current
    ) {
      return;
    }

    const maxDistance = getMaxHorizontalDistance();
    if (parseInt(desktopHorizontalDistance, 10) > maxDistance) {
      setIsRight(!isRight);
    }

    previousDistanceRef.current = desktopHorizontalDistance;
  }, [desktopHorizontalDistance, getMaxHorizontalDistance, isRight]);

  // Update flyout position when isRight changes
  useEffect(() => {
    if (flyoutRef.current) {
      const flyoutWidth = flyoutRef.current.offsetWidth;
      setFlyoutStyle((prev) => ({
        ...prev,
        left: isRight ? "auto" : horizontalDistance,
        right: isRight ? horizontalDistance : "auto",
      }));
    }
  }, [isRight]);

  // Create dropdown props object using the new interface
  const dropdownProps = {
    isOpen: isDropdownOpen,
    setIsOpen: setIsDropdownOpen,
    selectedLanguage,
    languages: availableLanguages,
  };

  return (
    <>
      <div id="" style={{ display: "none" }}></div>
      {!showLoader && (
        <FlyoutContainer
          flyoutRef={flyoutRef}
          isFlyoutOpen={isFlyoutOpen}
          isMobile={isMobile}
          style={flyoutStyle}
        >
          {/* HEADER SECTION  */}
          <Header
            isFeedbackActive={isFeedbackActive}
            setIsFeedbackActive={setIsFeedbackActive}
            setIsFlyoutOpen={setIsFlyoutOpen}
            isLargeScreen={isLargeScreen}
            startDrag={startDrag}
            dropdownRef={dropdownRef}
            dropdownProps={dropdownProps}
          />

          <div
            style={{
              overflowY: "auto",
              height: oversizeWidget
                ? "calc(100% - 200px)"
                : "calc(100% - 182px)",
              scrollbarWidth: "thin",
              paddingBottom: "30px",
              width: "100%",
            }}
          >
            {!isFeedbackActive ? (
              <>
                {/* ACTION BUTTONS SECTION  */}
                <MainFlyoutWidgetActionButtons handleClick={handleClick} />

                {/* ACCESSIBILITY FEATURES SECTION  */}
                <AccessibilityProfiles />

                {/* OVERSIZE WIDGET TOGGLE SECTION */}
                <div className="px-2 py-2">
                  <div className="accessibility-color flex items-center justify-between bg-[#F2F7FA] p-1 rounded-[20.5px]">
                    <div className="flex items-center space-x-3 p-2">
                      <img src={xlicon} alt="Blind Icon" className="w-5 h-3 " />
                      <p
                        className={twMerge(
                          clsx(
                            "flex items-start text-[20px] text-black",
                            oversizeWidget ? "font-semibold" : "font-[500px]"
                          )
                        )}
                      >
                        {t("over_size_widget")}
                      </p>
                    </div>
                    <ToggleSwitch
                      checked={oversizeWidget}
                      onChange={(e) => {
                        setOversizeWidget(e.target.checked);
                        localStorage.setItem(
                          "oversizeWidget",
                          JSON.stringify(e.target.checked)
                        );
                      }}
                      tabIndex={0}
                      style={{
                        backgroundColor: oversizeWidget ? buttonColor : "#ddd",
                        color: oversizeWidget ? "" : "blue",
                      }}
                      inputRef={inputRef}
                      containerClassName="rounded-full px-1 pt-2"
                    />
                  </div>
                </div>

                <AccessibilityAccordionCollection
                  isFlyoutOpen={isFlyoutOpen}
                  setIsModalOpen={setIsModalOpen}
                />
                {/* <hr /> */}
                <WidgetToggle
                  isFlyoutOpen={isFlyoutOpen}
                  isDragging={isDragging}
                  moveFlyoutToRight={() => {
                    setIsRight(true);
                  }}
                  moveFlyoutToLeft={() => {
                    setIsRight(false);
                  }}
                />
              </>
            ) : (
              <>
                {/* FEEDBACK FORM SECTION  */}
                <FeedBackUser setIsFeedbackActive={setIsFeedbackActive} />
              </>
            )}
          </div>

          {/* FOOTER SECTION  */}
          <MainFlyoutWidgetFooter
            isFeedbackActive={isFeedbackActive}
            setIsFeedbackActive={setIsFeedbackActive}
          />
        </FlyoutContainer>
      )}

      {/* WIDGET ICON SECTION - Floating icon that opens the menu */}
      {!showLoader && widgetData ? (
        <WidgetIcon
          isFlyoutOpen={isFlyoutOpen}
          toggleFlyout={toggleFlyout}
          size={iconSize}
        />
      ) : (
        <></>
      )}

      {/* MODAL SECTION - Shown when modal is open */}
      {isModalOpen && (
        <Modal
          modalRef={modalRef}
          closeModal={closeModal}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
        />
      )}
    </>
  );
};

export default React.memo(MainFlyoutWidget);
